import {
  Alert,
  type AssistantChatConversation,
  type AssistantChatMessage,
  AssistantChatPanel,
  type AssistantChatSidebarState,
  type AssistantChatTemplate,
  AssistantChatTemplatePicker,
  Icon,
} from "@assetwatch/ui";
import type { TFunction } from "i18next";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  EnterpriseApiError,
  getReliConversation,
  listAllReliConversations,
  type ReliChatMessage,
  type ReliClarificationSelection,
  sendReliMessage,
} from "../api/enterpriseClient";
import type {
  AskReliConversationRecord,
  AskReliDisplayMessage,
  AskReliSuggestedQuestion,
} from "./AskReli.types";
import { AskReliMarkdownContent } from "./snowflakes/AskReliMarkdownContent";
import { AskReliVisualizationBarChart } from "./snowflakes/AskReliVisualizationBarChart";

const WELCOME_MESSAGE_ID = "welcome";
const MAX_RELI_HISTORY_MESSAGES = 20;

// Prompts sent to the Ask Reli backend are kept in English on purpose: the
// backend SQL prompt and its exact-match known-query helper are English-tuned,
// so localized prompt text would degrade SQL quality or miss the canonical
// path. These strings are never displayed — only their localized titles are.
const TEMPLATE_PROMPTS = {
  focusToday: "What should I focus on today?",
  biggestWin: "What was my biggest win in the last 30 days?",
  missed: "What might I have missed?",
  enterpriseValue: "How can I get more value from Enterprise?",
} as const;

interface ReliAssistantPanelProps {
  open: boolean;
  customerId: number;
  onClose: () => void;
  minimized: boolean;
  onMinimize: () => void;
  onExpand: () => void;
  sidebar: AssistantChatSidebarState;
  onSidebarToggle: () => void;
}

function doesTextSelectOption(
  text: string,
  option: { id: string; label: string },
) {
  const normalizedText = text.trim().toLowerCase();
  const normalizedOptionId = option.id.toLowerCase();
  const normalizedOptionLabel = option.label.toLowerCase();

  return (
    normalizedOptionId === normalizedText ||
    normalizedOptionLabel === normalizedText ||
    normalizedText.includes(normalizedOptionLabel) ||
    (normalizedText.length >= 4 &&
      normalizedOptionLabel.includes(normalizedText))
  );
}

function doesTextExactlySelectOption(
  text: string,
  option: { id: string; label: string },
) {
  const normalizedText = text.trim().toLowerCase();

  return (
    option.id.toLowerCase() === normalizedText ||
    option.label.toLowerCase() === normalizedText
  );
}

function getClarificationSelection(
  text: string,
  clarification: AskReliDisplayMessage["clarification"],
): ReliClarificationSelection | undefined {
  if (!clarification) return undefined;

  const base = {
    clarificationId: clarification.id,
    question: clarification.question,
  };

  if (clarification.mode === "free_text") {
    return { ...base, mode: "free_text", response: text };
  }

  const selectedOptions = clarification.options.filter((option) =>
    doesTextSelectOption(text, option),
  );
  if (clarification.mode === "multi_select") {
    return selectedOptions.length > 0
      ? { ...base, mode: "multi_select", options: selectedOptions }
      : undefined;
  }

  const option =
    clarification.options.find((candidate) =>
      doesTextExactlySelectOption(text, candidate),
    ) ?? selectedOptions[0];
  return option ? { ...base, mode: "select", option } : undefined;
}

export function ReliAssistantPanel({
  open,
  customerId,
  onClose,
  minimized,
  onMinimize,
  onExpand,
  sidebar,
  onSidebarToggle,
}: ReliAssistantPanelProps) {
  const { i18n, t } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;

  const TEMPLATE_QUESTIONS = [
    {
      icon: "alerts-outline",
      id: "focus_today",
      title: t("enterprise.reliability.assistant.predefinedTitleFocusToday"),
      prompt: TEMPLATE_PROMPTS.focusToday,
    },
    {
      icon: "dollar-sign",
      id: "biggest_win",
      title: t("enterprise.reliability.assistant.predefinedTitleBiggestWin"),
      prompt: TEMPLATE_PROMPTS.biggestWin,
    },
    {
      icon: "clock-seven",
      id: "missed_items",
      title: t("enterprise.reliability.assistant.predefinedTitleMissed"),
      prompt: TEMPLATE_PROMPTS.missed,
    },
    {
      icon: "chart-line",
      id: "enterprise_value",
      title: t("enterprise.reliability.assistant.predefinedTitleValue"),
      prompt: TEMPLATE_PROMPTS.enterpriseValue,
    },
  ] as const satisfies readonly AskReliSuggestedQuestion[];

  const messageIdCounterRef = useRef(0);

  function createMessageId(prefix: string) {
    messageIdCounterRef.current += 1;
    return `${prefix}-${messageIdCounterRef.current}`;
  }

  function createConversationId() {
    return `conversation-${crypto.randomUUID()}`;
  }

  const [messages, setMessages] = useState<AskReliDisplayMessage[]>([]);
  const [activeConversationId, setActiveConversationId] =
    useState(createConversationId);
  const [conversations, setConversations] = useState<
    AskReliConversationRecord[]
  >([]);
  const [loadingConversationIds, setLoadingConversationIds] = useState<
    Set<string>
  >(() => new Set());
  const [error, setError] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(() => Date.now());
  const activeConversationIdRef = useRef(activeConversationId);
  const templateAbortControllerRef = useRef<AbortController | null>(null);
  const activeIsLoading = loadingConversationIds.has(activeConversationId);

  useEffect(() => {
    activeConversationIdRef.current = activeConversationId;
  }, [activeConversationId]);

  useEffect(
    () => () => {
      templateAbortControllerRef.current?.abort();
    },
    [],
  );

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    setError(null);
    void listAllReliConversations(customerId)
      .then((summaries) => {
        if (cancelled) return;

        setConversations((currentConversations) => {
          const currentById = new Map(
            currentConversations.map((conversation) => [
              conversation.id,
              conversation,
            ]),
          );
          const serverConversationIds = new Set(
            summaries.map((summary) => summary.conversationId),
          );

          const serverConversations = summaries.map((summary) => {
            const currentConversation = currentById.get(summary.conversationId);
            return {
              id: summary.conversationId,
              title:
                summary.title?.trim() ||
                t("enterprise.reliability.assistant.fallbackConversationTitle"),
              messages: currentConversation?.messages ?? [
                {
                  id: WELCOME_MESSAGE_ID,
                  role: "assistant" as const,
                  content: "",
                },
              ],
              updatedAt:
                currentConversation?.updatedAt ??
                (summary.updatedAt
                  ? new Date(summary.updatedAt).getTime()
                  : summary.createdAt
                    ? new Date(summary.createdAt).getTime()
                    : Date.now()),
              isLoading: currentConversation?.isLoading ?? false,
            };
          });

          return [
            ...serverConversations,
            ...currentConversations.filter(
              (conversation) => !serverConversationIds.has(conversation.id),
            ),
          ];
        });
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [customerId, t, open]);

  useEffect(() => {
    if (!activeIsLoading && messages.length === 0) {
      return;
    }

    setCurrentTime(Date.now());
    const interval = window.setInterval(
      () => setCurrentTime(Date.now()),
      60_000,
    );

    return () => window.clearInterval(interval);
  }, [activeIsLoading, messages.length]);

  function persistConversation(
    conversationId: string,
    nextMessages: AskReliDisplayMessage[],
  ) {
    const firstUserMessage = nextMessages.find(
      (message) => message.role === "user",
    );
    const title = firstUserMessage?.content.trim() || null;

    setConversations((current) => {
      const otherConversations = current.filter(
        (conversation) => conversation.id !== conversationId,
      );

      if (!title) {
        return otherConversations;
      }

      return [
        {
          id: conversationId,
          title,
          messages: nextMessages,
          updatedAt: Date.now(),
          isLoading: false,
        },
        ...otherConversations,
      ];
    });
  }

  function setConversationLoading(conversationId: string, loading: boolean) {
    setLoadingConversationIds((current) => {
      const nextLoadingConversationIds = new Set(current);

      if (loading) {
        nextLoadingConversationIds.add(conversationId);
      } else {
        nextLoadingConversationIds.delete(conversationId);
      }

      return nextLoadingConversationIds;
    });
  }

  function setMessagesIfActive(
    conversationId: string,
    nextMessages: AskReliDisplayMessage[],
  ) {
    if (activeConversationIdRef.current === conversationId) {
      setMessages(nextMessages);
    }
  }

  async function handleSend(messageText: string, signal: AbortSignal) {
    const text = messageText.trim();

    if (!text || activeIsLoading) return;

    setError(null);
    let conversationId = activeConversationId;
    if (
      messages.length === 0 &&
      conversations.some((conversation) => conversation.id === conversationId)
    ) {
      conversationId = createConversationId();
      activeConversationIdRef.current = conversationId;
      setActiveConversationId(conversationId);
    }
    const userMessage: AskReliDisplayMessage = {
      id: createMessageId("user"),
      role: "user",
      content: text,
      timestamp: Date.now(),
    };
    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);
    persistConversation(conversationId, nextMessages);
    setConversationLoading(conversationId, true);

    const history: ReliChatMessage[] = nextMessages
      .slice(0, -1)
      .slice(-MAX_RELI_HISTORY_MESSAGES)
      .map((m) => ({
        role: m.role,
        content: m.content,
        ...(m.role === "assistant"
          ? { clarification: m.clarification ?? null }
          : {}),
      }));
    const pendingClarification = [...messages]
      .reverse()
      .find((message) => message.role === "assistant")?.clarification;
    const clarificationSelection = getClarificationSelection(
      text,
      pendingClarification,
    );

    try {
      const result = await sendReliMessage(
        customerId,
        conversationId,
        text,
        history,
        clarificationSelection,
        pendingClarification ?? undefined,
        signal,
      );
      const completedMessages = [
        ...nextMessages,
        {
          id: createMessageId("assistant"),
          role: "assistant",
          content: result.answer,
          timestamp: Date.now(),
          rowCount: result.rowCount,
          visualization: result.visualization,
          clarification: result.clarification,
        },
      ] as AskReliDisplayMessage[];
      setMessagesIfActive(conversationId, completedMessages);
      persistConversation(conversationId, completedMessages);
    } catch (err) {
      if (signal.aborted) throw err;

      if (activeConversationIdRef.current === conversationId) {
        if (err instanceof EnterpriseApiError && err.status === 409) {
          setError(t("enterprise.reliability.assistant.errorConflict"));
          try {
            const canonicalConversation = await getReliConversation(
              customerId,
              conversationId,
            );
            const canonicalMessages = hydrateConversationMessages(
              conversationId,
              canonicalConversation.messages,
            );
            setConversations((currentConversations) =>
              currentConversations.map((currentConversation) =>
                currentConversation.id === conversationId
                  ? {
                      ...currentConversation,
                      messages: canonicalMessages,
                      isLoading: false,
                    }
                  : currentConversation,
              ),
            );
            setMessagesIfActive(conversationId, canonicalMessages);
          } catch {
            setMessagesIfActive(conversationId, messages);
          }
        } else if (
          err instanceof EnterpriseApiError &&
          (err.status === 502 || err.status === 503 || err.status === 504)
        ) {
          setError(t("enterprise.reliability.assistant.errorTimeout"));
        } else {
          setError(t("enterprise.reliability.assistant.errorGeneric"));
        }
        if (!(err instanceof EnterpriseApiError && err.status === 409)) {
          setMessagesIfActive(conversationId, nextMessages);
        }
      }
      persistConversation(conversationId, nextMessages);
      throw err;
    } finally {
      setConversationLoading(conversationId, false);
    }
  }

  function handleNewConversation() {
    const nextConversationId = createConversationId();
    activeConversationIdRef.current = nextConversationId;
    setActiveConversationId(nextConversationId);
    setMessages([]);
    setError(null);
  }

  async function handleSelectConversation(conversationId: string) {
    const conversation = conversations.find(
      (currentConversation) => currentConversation.id === conversationId,
    );
    if (!conversation) return;

    activeConversationIdRef.current = conversation.id;
    setActiveConversationId(conversation.id);
    setMessages(conversation.messages);
    setError(null);

    if (conversation.messages.some((message) => message.role === "user")) {
      return;
    }

    setConversationLoading(conversation.id, true);
    setConversations((current) =>
      current.map((currentConversation) =>
        currentConversation.id === conversation.id
          ? { ...currentConversation, isLoading: true }
          : currentConversation,
      ),
    );

    try {
      const result = await getReliConversation(customerId, conversation.id);
      const hydratedMessages = hydrateConversationMessages(
        conversation.id,
        result.messages,
      );
      setConversations((current) =>
        current.map((currentConversation) =>
          currentConversation.id === conversation.id
            ? {
                ...currentConversation,
                messages: hydratedMessages,
                isLoading: false,
              }
            : currentConversation,
        ),
      );
      setMessagesIfActive(conversation.id, hydratedMessages);
    } catch (err) {
      if (activeConversationIdRef.current === conversation.id) {
        setError(
          err instanceof EnterpriseApiError &&
            (err.status === 502 || err.status === 503 || err.status === 504)
            ? t("enterprise.reliability.assistant.errorTimeout")
            : t("enterprise.reliability.assistant.errorGeneric"),
        );
      }
    } finally {
      setConversationLoading(conversation.id, false);
      setConversations((current) =>
        current.map((currentConversation) =>
          currentConversation.id === conversation.id
            ? { ...currentConversation, isLoading: false }
            : currentConversation,
        ),
      );
    }
  }

  function handleTemplateSelect(template: AssistantChatTemplate) {
    const question = TEMPLATE_QUESTIONS.find(
      (item) => item.prompt === template.id,
    );
    if (!question) return;

    const controller = new AbortController();
    templateAbortControllerRef.current = controller;
    void handleSend(question.prompt, controller.signal).finally(() => {
      if (templateAbortControllerRef.current === controller) {
        templateAbortControllerRef.current = null;
      }
    });
  }

  const conversationHistory: AssistantChatConversation[] = conversations.map(
    (conv) => ({
      id: conv.id,
      title: conv.title,
      preview: conv.title.slice(0, 50),
      groupLabel: formatConversationGroupLabel(t, conv.updatedAt),
      timestamp: conv.updatedAt
        ? formatTimestamp(t, conv.updatedAt, currentTime)
        : undefined,
    }),
  );

  const activeMessages: AssistantChatMessage[] = messages.map((msg) => ({
    ...msg,
    author: msg.role === "assistant" ? "Ask Reli" : undefined,
    timestamp: msg.timestamp
      ? formatTimestamp(t, msg.timestamp, currentTime)
      : undefined,
  }));
  const showTemplatePicker = messages.length === 0 && !activeIsLoading;

  return (
    <div className={minimized ? "w-full max-[599px]:h-full" : "size-full"}>
      <AssistantChatPanel
        title={t("enterprise.reliability.assistant.title")}
        sidebar={sidebar}
        conversations={conversationHistory}
        activeConversationId={activeConversationId}
        conversationDate={formatConversationDate(locale, currentTime)}
        messages={showTemplatePicker ? [] : activeMessages}
        isResponding={activeIsLoading}
        minimized={minimized}
        onNewConversation={handleNewConversation}
        onSelectConversation={handleSelectConversation}
        onSidebarToggle={onSidebarToggle}
        collapseDirection="right"
        onSendMessage={handleSend}
        onClose={onClose}
        onMinimize={onMinimize}
        onExpand={onExpand}
        renderMessageContent={(msg) => {
          const originalMessage = messages.find(
            (message) => message.id === msg.id,
          );
          if (!originalMessage) return msg.content;

          if (originalMessage.role === "user") {
            return originalMessage.content;
          }

          return (
            <div className="flex flex-col gap-(--spacing-10)">
              <AskReliMarkdownContent content={originalMessage.content} />
              {originalMessage.visualization?.type === "bar" ? (
                <AskReliVisualizationBarChart
                  visualization={originalMessage.visualization}
                />
              ) : null}
            </div>
          );
        }}
        templatePicker={
          showTemplatePicker ? (
            <AssistantChatTemplatePicker
              title={t("enterprise.reliability.assistant.templatePickerTitle")}
              description={t(
                "enterprise.reliability.assistant.templatePickerDescription",
              )}
              templates={TEMPLATE_QUESTIONS.map((q) => ({
                id: q.prompt,
                title: q.title,
                icon: <Icon name={q.icon} size="md" tone="brand" />,
              }))}
              onSelectTemplate={handleTemplateSelect}
            />
          ) : null
        }
        composerProps={{
          disabled: activeIsLoading,
          alert: error ? <Alert tone="negative" title={error} /> : undefined,
          placeholder: t("enterprise.reliability.assistant.inputPlaceholder"),
        }}
      />
    </div>
  );
}

function hydrateConversationMessages(
  conversationId: string,
  history: ReliChatMessage[],
): AskReliDisplayMessage[] {
  return history.map((message, index) => ({
    id: `${conversationId}-${index}`,
    role: message.role,
    content: message.content,
    clarification: message.clarification ?? null,
  }));
}

function formatTimestamp(
  t: TFunction,
  timestamp: number,
  currentTime: number,
): string {
  const diff = currentTime - timestamp;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) {
    return t("enterprise.reliability.assistant.justNow");
  }
  if (minutes < 60) {
    return t("enterprise.reliability.assistant.minAgo", { count: minutes });
  }
  if (hours < 24) {
    return t("enterprise.reliability.assistant.hrAgo", { count: hours });
  }
  return t("enterprise.reliability.assistant.dayAgo", { count: days });
}

function formatConversationDate(language: string, timestamp: number) {
  return new Intl.DateTimeFormat(language, {
    day: "numeric",
    month: "long",
    weekday: "short",
  }).format(timestamp);
}

function formatConversationGroupLabel(t: TFunction, timestamp: number): string {
  const now = new Date();
  const date = new Date(timestamp);
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateStart = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );
  const daysAgo = Math.floor(
    (todayStart.getTime() - dateStart.getTime()) / (24 * 60 * 60 * 1000),
  );

  if (daysAgo <= 0) {
    return t("enterprise.reliability.assistant.historyToday");
  }
  if (daysAgo === 1) {
    return t("enterprise.reliability.assistant.historyYesterday");
  }
  if (daysAgo <= 7) {
    return t("enterprise.reliability.assistant.historyPrevious7Days");
  }
  return t("enterprise.reliability.assistant.historyOlder");
}
